import React, { FC, useState } from "react";
import { Footer } from "../../components/footer/Footer";
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import "../../styles/reset.scss";
import "./VoiceClone.scss";

interface Props {}

export const VoiceClone: FC<Props> = () => {
  const [voiceName, setVoiceName] = useState('');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadedAudio, setUploadedAudio] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { alertConfig, hideAlert } = useAlert();

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setUploadedAudio(url);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('audio/')) {
        const url = URL.createObjectURL(file);
        setUploadedAudio(url);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleCancel = () => {
    setVoiceName('');
    setGender('male');
    setLogoFile(null);
    setUploadedAudio(null);
  };

  const handleStartClone = () => {
    if (!voiceName.trim()) {
      alert('请输入声音名称');
      return;
    }
    if (!uploadedAudio) {
      alert('请上传音频文件');
      return;
    }
    
    setIsUploading(true);
    // 模拟上传过程
    setTimeout(() => {
      setIsUploading(false);
      alert('音色克隆已开始，请稍后查看结果');
    }, 2000);
  };

  return (
    <>
      <div className="voice-clone-main">
        {/* 使用封装的Alert组件 */}
        <AlertMessage
          config={alertConfig}
          onClose={hideAlert}
          zIndex={ALERT_Z_INDEX.VOICE_CLONE}
        />

        <div className="main-content">
          <div className="content-header">
            <h2>音色克隆</h2>
          </div>

          <div className="voice-clone-container">
            {/* 左侧表单区域 */}
            <div className="form-section">
              <div className="form-group">
                <label className="form-label required">声音名称</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                    placeholder="请输入声音名称"
                    maxLength={20}
                  />
                  <span className="char-counter">{voiceName.length}/20</span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">性别</label>
                <div className="gender-buttons">
                  <button
                    className={`gender-btn ${gender === 'male' ? 'active' : ''}`}
                    onClick={() => setGender('male')}
                  >
                    男
                  </button>
                  <button
                    className={`gender-btn ${gender === 'female' ? 'active' : ''}`}
                    onClick={() => setGender('female')}
                  >
                    女
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">声音Logo</label>
                <div className="logo-upload">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="logo-upload" className="logo-upload-btn">
                    <span className="logo-icon">+</span>
                    <span className="logo-text">添加</span>
                  </label>
                  {logoFile && (
                    <div className="logo-preview">
                      <img src={URL.createObjectURL(logoFile)} alt="Logo" />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">上传音频</label>
                <div className="audio-upload-section">
                  {!uploadedAudio ? (
                    <div
                      className="upload-area"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                    >
                      <input
                        type="file"
                        id="audio-upload"
                        accept=".m4a,.mp3,.wav"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="audio-upload" className="upload-label">
                        <div className="upload-icon">☁️</div>
                        <div className="upload-text">点击上传或拖拽文件到这里</div>
                      </label>
                    </div>
                  ) : (
                    <div className="audio-preview">
                      <audio controls src={uploadedAudio} className="audio-player" />
                      {/* <button
                        className="re-upload-btn"
                        onClick={() => setUploadedAudio(null)}
                      >
                        重新上传
                      </button> */}
                    </div>
                  )}

                  {isUploading && (
                    <div className="upload-loading">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">上传中...</div>
                    </div>
                  )}
                </div>
              </div>

              {/* 音频规格要求 */}
              <div className="audio-specs">
                <h4 className="specs-title">音频规格要求</h4>
                <div className="specs-content">
                  <div className="spec-item">
                    <span className="spec-label">音频时长:</span>
                    <span className="spec-value">支持上传10秒~2分钟、文件最大不可超过10M</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">音频格式:</span>
                    <span className="spec-value">支持m4a、mp3、wav格式</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">采样率:</span>
                    <span className="spec-value">16000Hz</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">声道:</span>
                    <span className="spec-value">单声道</span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button className="cancel-btn" onClick={handleCancel}>
                  取消
                </button>
                <button className="clone-btn" onClick={handleStartClone}>
                  开始克隆
                  <span className="clone-opportunities">有10次机会</span>
                </button>
              </div>
            </div>

            {/* 右侧说明区域 */}
            <div className="instructions-section">
              <h3 className="instructions-title">录制说明</h3>

              <div className="example-audio">
                <h4 className="example-title">示例音频</h4>
                <div className="audio-examples">
                  <div className="audio-example">
                    <div className="audio-info">
                      <span className="example-name">示例一</span>
                      <span className="audio-duration">0:51</span>
                    </div>
                    <div className="audio-player-sim">
                      <button className="play-btn">▶</button>
                      <div className="progress-bar">
                        <div className="progress-fill"></div>
                      </div>
                      <span className="current-time">0:00</span>
                      <button className="volume-btn">🔊</button>
                    </div>
                  </div>

                  <div className="audio-example">
                    <div className="audio-info">
                      <span className="example-name">示例二</span>
                      <span className="audio-duration">1:09</span>
                    </div>
                    <div className="audio-player-sim">
                      <button className="play-btn">▶</button>
                      <div className="progress-bar">
                        <div className="progress-fill"></div>
                      </div>
                      <span className="current-time">0:00</span>
                      <button className="volume-btn">🔊</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 音频录制要求 */}
              <div className="requirements-section">
                <h3 className="requirements-title">音频录制要求</h3>
                <div className="requirements-content">
                  <div className="requirement-item">
                    <span className="req-number">1.</span>
                    <span className="req-text">必须且仅有一个人说话,避免出现多人声音,确保全程说话清晰,无环境背景噪声、无明显回声和混响</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-number">2.</span>
                    <span className="req-text">保持音量大小、语速稳定、注意断句、避免口腔噪音(如口水声、喷麦等)、杂音、混响等</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-number">3.</span>
                    <span className="req-text">周围环境安静,减少人为噪声,如肢体碰撞声、服装摩擦声、桌椅移动声、鼠标键盘声等</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-number">4.</span>
                    <span className="req-text">可以存在口误,口误时无需终止录音,停顿1~2秒继续录制即可</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-number">5.</span>
                    <span className="req-text">为避免语气违和,需准备贴近最终使用场景的文本,如用于电商直播的声音使用电商带货文本进行录制</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};